import { memo, useCallback, useMemo } from "react";
import { t } from "i18next";
import { Link } from "@chakra-ui/react";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { ExpressCheckoutWithCardForm } from "./ExpressCheckoutWithCardForm";

export const SUBSCRIPTION_SETUP_PAYMENT_FORM_ID =
  "subscription-setup-payment-form";

type SubscriptionSetupPaymentProps = {
  clientSecret: string;
  setupIntentId: string;
  /**
   * Server-issued single-use nonce that the finalize endpoint requires.
   * Pass through to onComplete; without it the API rejects the call as
   * `Stripe setup nonce did not match.`.
   */
  clientNonce?: string;
  submitButtonText: string;
  disabled?: boolean;
  onComplete: (setupIntentId: string, clientNonce?: string) => void;
  onCancel?: () => void;
  /**
   * Set when the parent owns the primary submit CTA (typically an external
   * sticky button wired via the form id). Hides the form's own submit
   * button so users don't see two stacked CTAs.
   */
  hideInternalSubmitButton?: boolean;
  /** Mirrors the internal `submitting` flag up so the parent's external CTA
   *  can light up immediately on click, instead of waiting until Stripe
   *  resolves confirmSetup() ~2-3s later. */
  onSubmittingChange?: (submitting: boolean) => void;
};

function buildSetupReturnUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set("stripe_flow", "subscription_setup");
  url.searchParams.set("stripe_result", "success");
  return url.toString();
}

export const SubscriptionSetupPayment = memo(
  ({
    clientSecret,
    setupIntentId,
    clientNonce,
    submitButtonText,
    disabled,
    onComplete,
    onCancel,
    hideInternalSubmitButton,
    onSubmittingChange,
  }: SubscriptionSetupPaymentProps) => {
    const elementsOptions = useMemo(() => ({ clientSecret }), [clientSecret]);

    const onConfirm = useCallback(
      async ({
        stripe,
        elements,
      }: {
        stripe: Stripe;
        elements: StripeElements;
      }) => {
        const result = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: { return_url: buildSetupReturnUrl() },
          redirect: "if_required",
        });

        if (result.error) {
          throw new Error(
            result.error.message ??
              t("components.payments.subscription.subscriptionDeclined"),
          );
        }

        if (result.setupIntent?.status === "succeeded") {
          onComplete(result.setupIntent.id || setupIntentId, clientNonce);
          return;
        }

        throw new Error(
          t("components.payments.subscription.subscriptionDeclined"),
        );
      },
      [clientNonce, clientSecret, onComplete, setupIntentId],
    );

    return (
      <ExpressCheckoutWithCardForm
        formId={SUBSCRIPTION_SETUP_PAYMENT_FORM_ID}
        elementsOptions={elementsOptions}
        onConfirm={onConfirm}
        submitButtonText={submitButtonText}
        disabled={disabled}
        onCancel={onCancel}
        hideInternalSubmitButton={hideInternalSubmitButton}
        onSubmittingChange={onSubmittingChange}
        terms={
          <>
            By providing your card information, you allow GlobalComix to charge
            your card for future payments under the{" "}
            <Link
              href="/terms"
              isExternal
              color="dune.dune"
              textDecoration="underline"
            >
              GlobalComix Terms of Service
            </Link>
            .
          </>
        }
      />
    );
  },
);
